import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { useServerPagination } from '@/hooks/useServerPagination';
import { Search } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { formatBusinessDate } from '@/lib/format';

export default function InsuranceClaimsRecords() {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);

    const { data: countData } = useQuery({
        queryKey: ['insurance-claims-count', debouncedSearch],
        queryFn: async () => {
            let query = supabase.from('insurance_claims').select('*', { count: 'exact', head: true });
            if (debouncedSearch) query = query.ilike('description', `%${debouncedSearch}%`);
            const { count, error } = await query;
            if (error) throw error;
            return count || 0;
        },
    });

    const { currentPage, totalPages, totalItems, from, to, goToPage, resetPage } = useServerPagination({ totalCount: countData || 0 });

    useEffect(() => { resetPage(); }, [debouncedSearch]);

    const { data: claims, isLoading } = useQuery({
        queryKey: ['insurance-claims', currentPage, debouncedSearch],
        queryFn: async () => {
            let query = supabase
                .from('insurance_claims')
                .select('*, transactions(amount, transaction_at), vehicle_maintenances(vehicles(make, model, vehicle_registrations(number)))')
                .order('created_at', { ascending: false });
            if (debouncedSearch) query = query.ilike('description', `%${debouncedSearch}%`);
            query = query.range(from, to);
            const { data, error } = await query;
            if (error) throw error;
            return data;
        },
        enabled: countData !== undefined,
    });

    const formatCurrency = (amount: number) => new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);

    return (
        <Card className="border-0">
            <CardHeader>
                <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search claims..." className="pl-9" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
            </CardHeader>
            <CardContent>
                <ListPagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} />
                {isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : claims && claims.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Vehicle</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {claims.map((c) => {
                                const vehicleReg = c.vehicle_maintenances?.vehicles?.vehicle_registrations;
                                const regNumber = Array.isArray(vehicleReg) ? vehicleReg[0]?.number : vehicleReg?.number;
                                const vehicle = c.vehicle_maintenances?.vehicles;
                                return (
                                    <TableRow key={c.id}>
                                        <TableCell>
                                            {formatBusinessDate(c.transactions?.transaction_at ?? c.created_at)}
                                        </TableCell>
                                        <TableCell>
                                            {vehicle ? (
                                                <div>
                                                    <span className="font-medium">{regNumber || 'Unregistered'}</span>
                                                    <p className="text-xs text-muted-foreground">{vehicle.make} {vehicle.model}</p>
                                                </div>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell className="max-w-[200px] truncate">{c.description || '-'}</TableCell>
                                        <TableCell className="text-right font-medium">
                                            {c.transactions?.amount ? formatCurrency(c.transactions.amount) : '-'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-center text-muted-foreground py-8">No claims found</p>
                )}
            </CardContent>
        </Card>
    );
}
